import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { showOrganization, updateOrganization } from '~/javascripts/admin/actions/organization';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { Button } from '../../atoms/Buttons';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import Label from '../../atoms/Label';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import TextBox from '../../atoms/TextBox';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';

const PageWrapper = styled.div`
  ${Label} {
    margin-bottom: 0;
  }
`;

const FormInput = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '精算管理', href: '', isCurrentPage: true },
  { value: '振込元口座', href: '', isCurrentPage: true },
];

type AccountTypeForm = {
  bankName: string;
  bankCode: string;
  branchName: string;
  branchCode: string;
  accountType: 'saving' | 'current';
  bankAccount: string;
  originatorCode: string;
  originatorName: string;
};

const ClearingAccount = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AccountTypeForm>({
    defaultValues: {
      accountType: 'saving',
    },
  });

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const [apiMessage, setApiMessage] = useState<{
    text: string;
    isError: boolean;
  } | null>(null);

  useEffect(() => {
    if (userPrivateMoney.organization?.code) {
      showOrganization(userPrivateMoney.organization.code).then((data) =>
        reset({
          bankName: data.bank_name || '',
          bankCode: data.bank_code || '',
          branchName: data.bank_branch_name || '',
          branchCode: data.bank_branch_code || '',
          accountType: (data.bank_account_type as AccountTypeForm['accountType']) || 'saving',
          bankAccount: data.bank_account || '',
          originatorCode: data.bank_originator_code || '',
          originatorName: data.bank_originator_name || '',
        })
      );
    }
  }, [reset, userPrivateMoney.organization?.code]);

  const onSubmit = (data: AccountTypeForm) => {
    if (!userPrivateMoney.organization?.code) return;
    updateOrganization(
      userPrivateMoney.organization.code,
      {
        name: userPrivateMoney.organization.name,
        bank_name: data.bankName,
        bank_code: data.bankCode,
        bank_branch_name: data.branchName,
        bank_branch_code: data.branchCode,
        bank_account_type: data.accountType,
        bank_account: data.bankAccount,
        bank_originator_code: data.originatorCode,
        bank_originator_name: data.originatorName,
      },
      true
    )
      .then(() => setApiMessage({ text: '振込元口座の登録が完了しました', isError: false }))
      .catch((err) =>
        Promise.resolve(err).then((e) =>
          setApiMessage({ text: e.message ?? '予期しないエラーが発生しました', isError: true })
        )
      );
  };

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Grid.Container>
        <Grid.Item size={3}>
          <Label>組織名</Label>
        </Grid.Item>
        <Grid.Item size={5}>
          <Label>{userPrivateMoney.organization?.name}</Label>
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={3}>
          <Label>振込元口座</Label>
        </Grid.Item>

        <Grid.Item size={5} flexDirection="column" gap="16px">
          <FormInput>
            <Label>
              金融機関名 <Required />
            </Label>
            <TextBox
              fullWidth
              maxLength={15}
              {...register('bankName', { required: '金融機関名を入力してください' })}
            />
            <FormErrorMessage name="bankName" errors={errors} />
          </FormInput>

          <FormInput>
            <Label>
              金融機関コード <Required />
            </Label>
            <Controller
              name="bankCode"
              control={control}
              rules={{
                required: '金融機関コードを入力してください',
                minLength: { value: 4, message: '金融機関コードは4桁で入力してください' },
              }}
              render={({ field: { value, onChange } }) => (
                <TextBox
                  fullWidth
                  placeholder="例）1234"
                  maxLength={4}
                  syncValue={value}
                  onChange={(e) => /^\d*$/.test(e.target.value) && onChange(e)}
                />
              )}
            />

            <FormErrorMessage name="bankCode" errors={errors} />
          </FormInput>

          <FormInput>
            <Label>
              支店名 <Required />
            </Label>
            <TextBox
              fullWidth
              maxLength={15}
              {...register('branchName', { required: '支店名を入力してください' })}
            />
            <FormErrorMessage name="branchName" errors={errors} />
          </FormInput>

          <FormInput>
            <Label>
              支店コード <Required />
            </Label>
            <Controller
              name="branchCode"
              control={control}
              rules={{
                required: '支店コードを入力してください',
                minLength: { value: 3, message: '支店コードは3桁で入力してください' },
              }}
              render={({ field: { value, onChange } }) => (
                <TextBox
                  fullWidth
                  placeholder="例）123"
                  maxLength={3}
                  syncValue={value}
                  onChange={(e) => /^\d*$/.test(e.target.value) && onChange(e)}
                />
              )}
            />

            <FormErrorMessage name="branchCode" errors={errors} />
          </FormInput>

          <div>
            <Label>
              口座種別 <Required />
            </Label>

            <Controller
              name="accountType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioButtons>
                  <RadioGroup>
                    <Radio checked={value === 'saving'} onChange={() => onChange('saving')} />
                    <Label>普通</Label>
                  </RadioGroup>
                  <RadioGroup>
                    <Radio checked={value === 'current'} onChange={() => onChange('current')} />
                    <Label>当座</Label>
                  </RadioGroup>
                </RadioButtons>
              )}
            />
          </div>

          <FormInput>
            <Label>
              口座番号 <Required />
            </Label>
            <Controller
              name="bankAccount"
              control={control}
              rules={{
                required: '口座番号を入力してください',
                minLength: { value: 7, message: '口座番号は7桁で入力してください' },
              }}
              render={({ field: { value, onChange } }) => (
                <TextBox
                  fullWidth
                  placeholder="例）1234567"
                  maxLength={7}
                  syncValue={value}
                  onChange={(e) => /^\d*$/.test(e.target.value) && onChange(e)}
                />
              )}
            />
            <FormErrorMessage name="bankAccount" errors={errors} />
          </FormInput>

          <FormInput>
            <Label>
              委託者コード <Required />
            </Label>
            <Controller
              name="originatorCode"
              control={control}
              rules={{
                required: '委託者コードを入力してください',
                minLength: { value: 10, message: '委託者コードは10桁で入力してください' },
              }}
              render={({ field: { value, onChange } }) => (
                <TextBox
                  fullWidth
                  placeholder="例）0123456789"
                  maxLength={10}
                  syncValue={value}
                  onChange={(e) => /^\d*$/.test(e.target.value) && onChange(e)}
                />
              )}
            />

            <FormErrorMessage name="originatorCode" errors={errors} />
          </FormInput>

          <FormInput>
            <Label>
              委託者名 <Required />
            </Label>
            <TextBox
              fullWidth
              maxLength={40}
              placeholder="例）ﾔﾏﾀﾞﾀﾛｳ"
              {...register('originatorName', { required: '委託者名を入力してください' })}
            />
            <FormErrorMessage name="originatorName" errors={errors} />
          </FormInput>
        </Grid.Item>

        <Grid.Divider />
      </Grid.Container>

      {apiMessage && <ApiResults messages={[apiMessage]} />}

      <Button onClick={handleSubmit(onSubmit)}>登録</Button>
    </PageWrapper>
  );
};

export default ClearingAccount;
